import { UsersRound } from 'lucide-react';
import { createSearchParams, useNavigate } from 'react-router';
import { useSuspenseQuery } from '@apollo/client/react';

import { EscrowMembershipsOrderByEnum, QueryOrderEnum, UserRoleEnum } from 'src/graphql/schema';
import { EscrowMembershipsDocument } from 'src/graphql/actions/escrowMemberships/escrowMemberships.query';
import { ProfileDocument } from 'src/graphql/actions/profile/profile.query';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { getGravatarUrl } from 'src/utils/getGravatarUrl';
import { getInitials } from 'src/widgets/AppCard/model';
import { roleOptionsDict } from 'src/containers/Escrow/Memberships';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { withSuspense } from 'src/hoc';
import { getMembershipSortPriority } from 'src/containers/Escrow/Memberships/Management';

import SummaryCard from './SummaryCard';

type Props = {
  escrowId: string;
  beneficiaryId: string;
  basePath: string;
};

const MembersList = withSuspense(({ beneficiaryId }: Pick<Props, 'beneficiaryId'>) => {
  const {
    data: { escrowMemberships },
  } = useSuspenseQuery(EscrowMembershipsDocument, {
    variables: {
      beneficiaryId,
      page: 1,
      perPage: 100,
      orderBy: EscrowMembershipsOrderByEnum.Role,
      order: QueryOrderEnum.Asc,
    },
  });
  const {
    data: { profile },
  } = useSuspenseQuery(ProfileDocument);

  const memberships = escrowMemberships.nodes
    .slice()
    .sort(
      (a, b) =>
        getMembershipSortPriority(a.appSubscription.user, profile.id) -
        getMembershipSortPriority(b.appSubscription.user, profile.id),
    );

  return (
    <ul aria-label="Escrow members" className="divide-y">
      {memberships.map(({ id, role, appSubscription: { user } }) => (
        <li key={id} className="flex items-center gap-2 py-2.5">
          <Avatar className="size-6 rounded-md shrink-0">
            <AvatarImage src={getGravatarUrl(user.email ?? '')} />
            <AvatarFallback className="rounded-md bg-slate-200 text-slate-600 text-2xs font-semibold">
              {getInitials(user.name ?? '')}
            </AvatarFallback>
          </Avatar>
          <span className="text-sm text-foreground truncate">{user.name || '-'}</span>
          {user.role === UserRoleEnum.Owner ? (
            <Badge
              variant="pill"
              className="border-0 whitespace-nowrap text-2xs bg-foreground text-white py-0.5 px-1.5"
            >
              Account owner
            </Badge>
          ) : (
            user.id === profile.id && (
              <Badge variant="pill" className="border-0 whitespace-nowrap text-2xs py-0.5 px-1.5">
                You
              </Badge>
            )
          )}
          <span className="ml-auto shrink-0 text-xs text-muted-foreground">{roleOptionsDict[role]}</span>
        </li>
      ))}
    </ul>
  );
});

const MembersCard = ({ escrowId, beneficiaryId, basePath }: Props) => {
  const navigate = useNavigate();

  const onEdit = () =>
    navigate({
      pathname: `${basePath}/onboarding/${escrowId}/members`,
      search: createSearchParams({
        isEdit: 'true',
      }).toString(),
    });

  return (
    <SummaryCard
      icon={UsersRound}
      title="Escrow members"
      description="Team members for this specific escrow and their assigned permissions."
      action={
        <Button variant="secondary" size="sm" onClick={onEdit}>
          Edit
        </Button>
      }
    >
      <MembersList beneficiaryId={beneficiaryId} />
    </SummaryCard>
  );
};

export default MembersCard;
